const LEGACY_TRADE_TIME_FIELDS = {
  broker1: {
    open: "broker1OpenTime",
    close: "broker1CloseTime",
  },
  broker2: {
    open: "broker2OpenTime",
    close: "broker2CloseTime",
  },
};

const FINAL_TRADE_TIME_FIELDS = {
  open: "openedAtUtc",
  close: "closedAtUtc",
};
const RAW_TERMINAL_TIMESTAMP_RE = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?$/;

function pad2(value) {
  return String(value).padStart(2, "0");
}

export function parseTradeTimestampMs(value) {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    return value < 1e12 ? value * 1000 : value;
  }

  const input = String(value).trim();
  if (!input) return null;

  if (/^[+-]?\d+$/.test(input)) {
    const numeric = Number(input);
    if (!Number.isFinite(numeric)) return null;
    return Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
  }

  const parsed = Date.parse(input);
  return Number.isFinite(parsed) ? parsed : null;
}

function getLegacyTradeTimeField(brokerKey, phase) {
  return LEGACY_TRADE_TIME_FIELDS[brokerKey]?.[phase] || null;
}

function getTradeTimeNode(trade, brokerKey, phase) {
  return trade?.tradeTimes?.[brokerKey]?.[phase] || null;
}

function normalizeRawTerminalTimestamp(value) {
  if (value === null || value === undefined || value === "") return null;
  const input = String(value).trim();
  if (!input) return null;

  const match = RAW_TERMINAL_TIMESTAMP_RE.exec(input);
  if (!match) return null;

  const second = pad2(match[6] || "00");
  return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${second}`;
}

function toUtcClockKey(value) {
  const ms = parseTradeTimestampMs(value);
  if (ms === null) return null;

  const date = new Date(ms);
  return (
    [date.getUTCFullYear(), pad2(date.getUTCMonth() + 1), pad2(date.getUTCDate())].join("-") +
    `T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:${pad2(date.getUTCSeconds())}`
  );
}

export function formatTradeTerminalTime(value, fallback = "—") {
  const raw = normalizeRawTerminalTimestamp(value) || toUtcClockKey(value);
  if (!raw) return fallback;

  const match = RAW_TERMINAL_TIMESTAMP_RE.exec(raw);
  if (!match) return fallback;

  return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}:${pad2(match[6] || "00")}`;
}

export function getTradeBrokerTerminalTimeKey(trade, brokerKey, phase) {
  const node = getTradeTimeNode(trade, brokerKey, phase);
  const legacyField = getLegacyTradeTimeField(brokerKey, phase);
  const legacyValue = legacyField ? trade?.[legacyField] : null;

  return (
    normalizeRawTerminalTimestamp(node?.raw) ||
    toUtcClockKey(node?.utc) ||
    toUtcClockKey(legacyValue) ||
    null
  );
}

export function getTradeFinalPhaseTerminalTimeKey(trade, phase) {
  const candidates = [
    {
      key: getTradeBrokerTerminalTimeKey(trade, "broker1", phase),
      timestampMs: getTradeBrokerDisplayTimestampMs(trade, "broker1", phase),
    },
    {
      key: getTradeBrokerTerminalTimeKey(trade, "broker2", phase),
      timestampMs: getTradeBrokerDisplayTimestampMs(trade, "broker2", phase),
    },
  ].filter((candidate) => candidate.key || candidate.timestampMs !== null);

  if (!candidates.length) {
    return toUtcClockKey(getTradeFinalPhaseTime(trade, phase));
  }

  const candidatesWithTimestamp = candidates.filter((candidate) => candidate.timestampMs !== null);
  if (candidatesWithTimestamp.length > 0) {
    const sortedCandidates = [...candidatesWithTimestamp].sort((left, right) => {
      if (left.timestampMs !== right.timestampMs) {
        return phase === "open"
          ? left.timestampMs - right.timestampMs
          : right.timestampMs - left.timestampMs;
      }

      return String(left.key || "").localeCompare(String(right.key || ""));
    });

    const selected = sortedCandidates[0];
    return selected.key || toUtcClockKey(selected.timestampMs);
  }

  const fallbackKeys = candidates.map((candidate) => candidate.key).filter(Boolean);

  if (!fallbackKeys.length) return null;
  return phase === "open" ? [...fallbackKeys].sort()[0] : [...fallbackKeys].sort().slice(-1)[0];
}

export function getTradeBrokerDisplayTime(trade, brokerKey, phase) {
  const node = getTradeTimeNode(trade, brokerKey, phase);
  const legacyField = getLegacyTradeTimeField(brokerKey, phase);
  const legacyValue = legacyField ? trade?.[legacyField] : null;
  const nodeUtc = parseTradeTimestampMs(node?.utc) !== null ? node.utc : null;
  const legacyTime = parseTradeTimestampMs(legacyValue) !== null ? legacyValue : null;
  const rawTime = parseTradeTimestampMs(node?.raw) !== null ? node.raw : null;

  return nodeUtc || legacyTime || rawTime || null;
}

export function getTradeBrokerDisplayTimestampMs(trade, brokerKey, phase) {
  return parseTradeTimestampMs(getTradeBrokerDisplayTime(trade, brokerKey, phase));
}

export function getTradeFinalPhaseTime(trade, phase) {
  const finalField = FINAL_TRADE_TIME_FIELDS[phase];
  const finalValue = finalField ? trade?.tradeTimes?.final?.[finalField] : null;
  if (parseTradeTimestampMs(finalValue) !== null) return finalValue;

  const timestamps = [
    getTradeBrokerDisplayTimestampMs(trade, "broker1", phase),
    getTradeBrokerDisplayTimestampMs(trade, "broker2", phase),
  ].filter((value) => value !== null);

  if (timestamps.length === 0) return null;

  const picked = phase === "open" ? Math.min(...timestamps) : Math.max(...timestamps);

  return new Date(picked).toISOString();
}

export function getTradeFinalPhaseTimestampMs(trade, phase) {
  return parseTradeTimestampMs(getTradeFinalPhaseTime(trade, phase));
}
